<!-- src/components/ri3/department/DepartmentOrgChart.vue -->
<template>
  <div class="org-chart-wrapper bgTable col-12" ref="orgTree" @click="onClickTree"></div>
</template>

<script>
import ApexTree from "apextree";

// helpers
function toNumber(v){ 
  if(v===null||v===undefined||v==="") return null; 
  const n=Number(v); 
  return Number.isFinite(n)?n:null; 
}

function clamp(v, min, max){ 
  return Math.max(min, Math.min(max, v)); 
}

function fmt(n){ 
  if(n===null||n===undefined) return "—"; 
  try { 
    return new Intl.NumberFormat("fr-FR",{maximumFractionDigits:0}).format(Number(n)) 
  } catch { 
    return String(n) 
  } 
}

function percent(consumption, budget){
  const c = toNumber(consumption) ?? 0;
  const b = toNumber(budget);
  if(!b || b <= 0) return null;
  return clamp(Math.round((c / b) * 100), 0, 999);
}

export default {
  name: "DepartmentOrgChart",
  props: {
    items: { type: Array, required: true },
    /** true = % sur node + descendants ; false = node seul */
    useTreeProgress: { type: Boolean, default: true },
  },
  data(){ 
    return { 
      tree: null, 
      resizeHandler: null 
    }; 
  },
  mounted(){
    this.resizeHandler = () => this.renderTree();
    window.addEventListener("resize", this.resizeHandler);
    this.renderTree();
  },
  beforeDestroy(){
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler);
    if (this.tree) {
      if (typeof this.tree.destroy === "function") this.tree.destroy();
      else if (typeof this.tree.dispose === "function") this.tree.dispose();
      else if (this.tree.clear) this.tree.clear();
      this.tree = null;
    }
  },
  watch:{ 
    items:{ 
      deep:true, 
      handler(){ 
        this.renderTree(); 
      } 
    } 
  },
  methods: {
    renderTree(){
      const el = this.$refs.orgTree;
      if(!el || !this.items || !this.items.length) return;
      
      const data = this.buildTreeData(this.items);
      if(!data) return;
      
      const options = {
        width: el.clientWidth || 800,
        height: 740,
        direction: "top",
        childrenSpacing: 64,
        siblingSpacing: 44,
        nodeWidth: 320,
        nodeHeight: 150,
        nodeBGColor: "#FFFFFF",
        nodeBGColorHover: "#F8FAFC",
        borderColor: "rgba(16,24,40,0.12)",
        borderColorHover: "rgba(16,24,40,0.18)",
        borderWidth: 1,
        borderRadius: "10px",
        contentKey: "name",
        nodeTemplate: (content) => {
          const raw = String(content ?? "");
          const [title, payload, type] = raw.split("||");
          
          // ===== Carte SOCIÉTÉ (racine) =====
          if (type === "company") {
            const name = (title || "").toUpperCase();
            return `
              <div class="node-card node-company node-flat" data-id="${title}" data-type="company">
                <div class="company-center">
                  <div class="company-name" title="${name}">${name}</div>
                  <div class="company-subtitle">
                    Organigramme budgets / bons carburant par département
                  </div>
                </div>
              </div>
            `;
          }
          
          // payload pour département: "budget|consoBons|pctBons|consoReel|pctReel"
          let b="—", cB="—", pB=null, cR="—", pR=null;
          if (payload) {
            const parts = payload.split("|");
            b  = parts[0] ?? "—";
            cB = parts[1] ?? "—";
            const pBraw = parts[2];
            cR = parts[3] ?? "—";
            const pRraw = parts[4];
            
            if (pBraw !== undefined && pBraw !== "—" && pBraw !== "") {
              const n = Number(pBraw); 
              if (Number.isFinite(n)) pB = clamp(n, 0, 100);
            }
            if (pRraw !== undefined && pRraw !== "—" && pRraw !== "") {
              const n = Number(pRraw); 
              if (Number.isFinite(n)) pR = clamp(n, 0, 100);
            }
          }
          
          // Sévérité = pire des deux %
          const worst = (pB === null && pR === null) ? null :
                        (pB === null ? pR : (pR === null ? pB : Math.max(pB, pR)));
          const band =
            worst === null ? "neutral" :
            worst < 60 ? "ok" :
            worst < 85 ? "warn" : "alert";
          
          // Textes "Bons XX%" et "Réel XX%"
          const bonsPercentText = pB === null ? "—" : `${pB}%`;
          const reelPercentText = pR === null ? "—" : `${pR}%`;

          const rowBons = `
            <div class="progress-row">
              <span class="prog-label label-bons">Bons ${bonsPercentText}</span>
              <div class="track"><div class="bar bar-bons" style="width:${pB===null?0:pB}%;"></div></div>
            </div>
          `;
          const rowReel = `
            <div class="progress-row">
              <span class="prog-label label-reel">Réel ${reelPercentText}</span>
              <div class="track"><div class="bar bar-reel" style="width:${pR===null?0:pR}%;"></div></div>
            </div>
          `;
          
          return `
            <div class="node-card node-flat node-band-${band}" data-id="${title}" data-type="department">
              <div class="node-header">
                <div class="node-title">
                  <span class="node-dot dot-${band}"></span>
                  <span class="node-name" title="${title ?? ""}">${title ?? ""}</span>
                </div>
              </div>
              <div class="progress-dual">
                ${rowBons}
                ${rowReel}
              </div>
              <div class="node-body grid-2">
                <div class="meta meta-wide">
                  <span class="meta-label">Budget</span>
                  <span class="meta-value">${b} XOF</span>
                </div>
                <div class="meta meta-wide">
                  <span class="meta-label">Conso (bons)</span>
                  <span class="meta-value">${cB} XOF</span>
                </div>
                <div class="meta meta-wide">
                  <span class="meta-label">Conso (réel)</span>
                  <span class="meta-value">${cR} XOF</span>
                </div>
              </div>
            </div>
          `;
        }
      };
      
      if(this.tree){
        this.tree.updateOptions({ width: options.width, height: options.height });
        if (typeof this.tree.render === "function") this.tree.render(data);
      } else {
        this.tree = new ApexTree(el, options);
        if (typeof this.tree.render === "function") this.tree.render(data);
      }
    },
    
    buildTreeData(items){
      const map = {};
      const roots = [];
      
      // 1. Créer tous les noeuds département
      items.forEach(item => {
        const title = item?.name ?? "(sans nom)";
        const budget = item?.budget;
        
        // ---- Conso via BONS (montant total des bons pour le département)
        const bonsSelf  = item?.period_total_amount ?? null;
        const bonsTree  = item?.period_total_amount_tree ?? null;
        const consoBons = this.useTreeProgress ? (bonsTree ?? bonsSelf ?? null)
                                               : (bonsSelf ?? bonsTree ?? null);
        
        // ---- Conso RÉELLE (utilisation réelle via Activity)
        const realSelf  = item?.period_total_amount_real ?? null;
        const realTree  = item?.period_total_amount_tree_real ?? null;
        const consoReel = this.useTreeProgress ? (realTree ?? realSelf ?? null)
                                               : (realSelf ?? realTree ?? null);
        
        const pctBons = percent(consoBons, budget);
        const pctReel = percent(consoReel, budget);
        
        const budgetStr = fmt(budget);
        const bonsStr   = fmt(consoBons);
        const reelStr   = fmt(consoReel);
        
        // payload: "budget|consoBons|pctBons|consoReel|pctReel"
        const payload = `${budgetStr}|${bonsStr}|${pctBons===null?"—":String(pctBons)}|${reelStr}|${pctReel===null?"—":String(pctReel)}`;
        
        map[item.id] = {
          id: String(item.id),
          name: `${title}||${payload}||department`,
          children: [],
          _raw: item
        };
      });
      
      // 2. Construire la hiérarchie des départements
      items.forEach(item => {
        const parentId = item?.parent_object?.id ?? item?.parent ?? null;
        if (parentId && map[parentId]) {
          map[parentId].children.push(map[item.id]);
        } else {
          roots.push(map[item.id]);
        }
      });
      
      // 3. Créer le noeud Company (racine)
      const companyName = this.$store?.state?.app?.company?.name || "Entreprise";
      const companyNode = {
        id: "company-root",
        name: `${companyName}||||company`,
        children: roots
      };
      
      return companyNode;
    },
    
    onClickTree(evt){
      let el = evt.target;
      const container = this.$refs.orgTree;
      
      while (el && el !== container && !el.classList?.contains("node-card")) {
        el = el.parentNode;
      }
      
      if (!el || el === container) return;
      
      const nodeType = el.getAttribute("data-type");
      if (nodeType === "company") return;
      
      const title = el.getAttribute("data-id");
      const item = this.items.find(i => i.name === title);
      
      if (item) this.$emit("select", item);
    }
  }
};
</script>

<style>
.org-chart-wrapper {
  width: 100%;
  min-height: 740px;
  padding: 18px;
  background: #f4f6fa;
  border-radius: 12px;
  overflow: auto;
  border: 1px solid rgba(108,108,108,.6);
}

/* === Carte générique === */
.node-card {
  width: 318px;
  height: 148px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid black !important;
  border-radius: 10px;
  padding: 10px 12px;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.apextree-node {
  border: none !important;
}

.node-flat { 
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06); 
}

.node-card:hover {
  transform: translateY(-1px);
  border-color: rgba(15, 23, 42, 0.2);
  box-shadow: 0 4px 10px rgba(16, 24, 40, 0.08);
  cursor: pointer;
}

/* Bordures selon sévérité */
.node-band-ok {
  border-color: rgba(22, 163, 74, 0.25);
}

.node-band-warn {
  border-color: rgba(245, 158, 11, 0.35);
}

.node-band-alert {
  border-color: rgba(239, 68, 68, 0.45);
}

.node-band-neutral {
  border-color: rgba(100, 116, 139, 0.25);
}

/* === Noeud Company (racine) – même dimension, fond coloré === */
.node-company {
  background: linear-gradient(135deg, #4f46e5 0%, #38bdf8 100%);
  border-color: rgba(59,130,246,0.8);
  color: #ffffff;
}

.company-center {
  width: 100%;
  height: 100%;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  text-align:center;
  padding: 4px;
}

.company-name {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .8px;
  text-transform: uppercase;
  white-space: normal;
  word-break: break-word;
  margin-bottom: 6px;
}

.company-subtitle {
  font-size: 12px;
  color: rgba(255,255,255,0.9);
}

/* === Header des cartes département === */
.node-header { 
  display:flex; 
  align-items:center; 
  justify-content:space-between; 
  margin-bottom: 6px; 
}

.node-title { 
  display:inline-flex; 
  align-items:center; 
  gap:8px; 
  max-width: 220px; 
}

.node-name { 
  font-weight:600; 
  font-size:14px; 
  color:#0f172a; 
  white-space:nowrap; 
  overflow:hidden; 
  text-overflow:ellipsis; 
}

.node-dot { 
  width:8px; 
  height:8px; 
  border-radius:999px; 
  display:inline-block; 
}

.dot-ok{background:#16a34a;} 
.dot-warn{background:#f59e0b;} 
.dot-alert{background:#ef4444;} 
.dot-neutral{background:#64748b;}
.dot-company{background:#ffffff;}

/* Deux barres */
.progress-dual { 
  display:flex; 
  flex-direction:column; 
  gap:6px; 
  margin:4px 0 6px 0; 
}

.progress-row { 
  display:grid; 
  grid-template-columns: 120px 1fr; 
  align-items:center; 
  gap:8px; 
}

.prog-label { 
  font-size:11px; 
  font-weight:700; 
  letter-spacing:.1px; 
}

.label-bons { 
  color:#FF7A00; 
}

.label-reel { 
  color:#0EB5A3; 
}

/* Mode dark */
.dark-mode .node-name { 
  color: #ffffff; 
}

.dark-mode .meta-value { 
  color: #ffffff; 
}

.track {
  position: relative;
  width:100%;
  height:8px;
  border-radius:999px;
  background:#eef2f7;
  overflow:hidden;
}

.bar {
  height:100%;
  border-radius:999px;
  transition: width .25s ease;
}

.bar-bons { 
  background:#FF8A00; 
}

.bar-reel { 
  background:#10B981; 
}

/* Infos (Budget pleine largeur, puis 2 colonnes) */
.node-body.grid-2 { 
  display:grid; 
  grid-template-columns: 1fr 1fr; 
  column-gap:10px; 
  row-gap:4px; 
}

.meta { 
  display:flex; 
  align-items:center; 
  justify-content:space-between; 
}

.meta-wide { 
  grid-column: 1 / -1; 
}

.meta-label { 
  color:#64748b; 
  font-size:11px; 
}

.meta-value { 
  color:#0f172a; 
  font-size:12px; 
  font-weight:600; 
}
</style>
